import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, HStack, VStack, Text, Badge, Alert, AlertIcon, Spinner, Code, Divider,
  Button, Collapse,
} from "@chakra-ui/react";
import { readCache, writeCache, formatAge, MEMORY_ISSUES_KEY, MEMORY_SCAN_KEY } from "../../../utils/resultCache";
import AssignModal from "../../../components/messaging/AssignModal";
import { SourceTaskChip, useSourceTasks } from "./issueTasks";
import { ImpactChip, ImpactDonut } from "./StatusDonut";
import {
  byImpact, findingEffect, findingImpact, impact as makeImpact, memoryEffect, notEstimated, pctOf, planLabel, stateOf,
  withEffect, worseOf,
} from "./impact";
import { AiFindingCard, ClosedFindings, aiFindingTaskText, useCodeFindings, SizingNote, useSizing } from "./AiFindings";
import { useLastNight, pickNewer, LastNightLabel, serverLabel } from "./LastNight";

/**
 * Second tab of the Live Activity modal: what can cost this server memory, grouped by layer.
 * Three kinds of item, and none of them is a fixed list:
 *   - live checks (the catalogue in backend/api/memory_issues): Verify runs one against this
 *     backend's process, and it turns green once its fix is deployed;
 *   - code patterns: "Scan code" looks through the whole backend for code that costs memory
 *     (backend/api/memory_issues/scan.py); each pattern lists where it was found;
 *   - the nightly AI review's memory findings in each day's commits, open until a commit fixes
 *     them or someone dismisses them.
 * The server also runs every check and the scan by itself each night that had commits; those
 * results show, labelled, unless one run from this browser is newer. Verify results persist in
 * localStorage like the HIPAA de-id tab's tests.
 *
 * How bad each one is: its memory as a share of the Render plan's limit (status.limit_mb, the
 * number the Live Activity memory chart draws against). A failing check costs its issue's cost_mb,
 * a code pattern its MB per place times the places found, and an AI finding the review's estimate.
 * The summary is the tab's health from those (impact.js), so small leaks don't paint it red.
 */

const LAYER = {
  floor: { label: "FLOOR", scheme: "gray", blurb: "the idle floor: process count and import weight" },
  climber: { label: "CLIMBER", scheme: "orange", blurb: "slow climbers: state that grows with normal use" },
  spike: { label: "SPIKE", scheme: "red", blurb: "spike killers: one action adds 200 MB to 1 GB" },
  admin: { label: "ADMIN", scheme: "purple", blurb: "rare admin actions" },
  other: { label: "OTHER", scheme: "gray", blurb: "memory problems the nightly AI review found that fit no layer" },
};

const SCAN_VERDICT = {
  warning: { label: "FOUND", scheme: "red" },
  ok: { label: "NONE FOUND", scheme: "green" },
  unknown: { label: "COULDN'T CHECK", scheme: "gray" },
};
// A task gets at most this many places; the rest stay on this tab.
const TASK_PLACES = 15;
const PLACES_SHOWN = 50;

const VERDICT = {
  pass: { label: "PASS", scheme: "green" },
  fail: { label: "FAIL", scheme: "red" },
  na: { label: "N/A", scheme: "gray" },
  error: { label: "ERROR", scheme: "red" },
};

// What the summary says for each state of the tab's health (impact.js).
const TONE_OF_STATE = { urgent: "red", minor: "yellow", good: "green" };
const LIMIT_SOURCE = {
  "this server": "this server's limit",
  "the Render plan": "the Render server's limit",
  default: "assumed: no limit was found",
};
const DEFAULT_LIMIT_MB = 2048;

function limitOf(...candidates) {
  const found = candidates.map(Number).find((mb) => mb > 0);
  return found || DEFAULT_LIMIT_MB;
}

/** A live check's impact: from the server's verdict when it sent one, else its cost_mb against the
 * plan (a result saved in this browser before impact existed). null = not checked or couldn't check. */
export function probeImpact(issue, result, limitMb) {
  if (!result) return null;
  const of = planLabel(limitMb);
  if (result.impact !== undefined) return result.impact ? { ...result.impact, of } : null;
  if (result.status === "pass" || result.status === "na") return makeImpact(0, of);
  if (result.status !== "fail") return null;
  const mb = Number(issue?.cost_mb);
  return mb > 0 ? makeImpact((mb / limitOf(limitMb)) * 100, of, `About ${mb} MB of ${of}.`) : notEstimated(of);
}

/** A code pattern's impact: from the scan (MB per place times the places found), or not estimated
 * for a scan saved before impact existed. */
export function patternImpact(result, limitMb) {
  if (!result) return null;
  const of = planLabel(limitMb);
  if (result.impact !== undefined) return result.impact ? { ...result.impact, of } : null;
  if (result.status === "ok") return makeImpact(0, of);
  return (result.count || 0) > 0 ? notEstimated(of) : null;
}

/** An AI memory finding's impact, as a share of the plan. */
export function memoryFindingImpact(finding, limitMb) {
  return { ...findingImpact(finding), of: planLabel(limitMb) };
}

function readAllCached() {
  return readCache(MEMORY_ISSUES_KEY)?.data ?? {};
}

function cacheResult(id, result) {
  const all = readAllCached();
  all[id] = { result, savedAt: new Date().toISOString() };
  writeCache(MEMORY_ISSUES_KEY, all);
}

// Developers most familiar with the failing code, from git history (hard-coded per
// issue on the backend). Only worth showing when the last Verify did not pass.
export function suggestedOwners(issue, result) {
  if (!result || !["fail", "error"].includes(result.status)) return [];
  return Array.isArray(issue.owners) ? issue.owners : [];
}

// Title and description for the task, from the issue and its last Verify.
export function issueTaskText(issue, entry) {
  const result = entry?.result || null;
  const lines = [
    issue.cost && `Cost: ${issue.cost}`,
    issue.why && `Why it matters: ${issue.why}`,
    `What makes it pass: ${result?.fix || issue.fix}`,
    issue.trigger && `Trigger: ${issue.trigger}`,
    `Review ref ${issue.ref} · issue id ${issue.id}`,
  ];
  if (result) {
    const failing = (result.checks || []).filter((c) => c.passed === false).map((c) => c.name);
    const when = entry.savedAt ? ` (${new Date(entry.savedAt).toLocaleString()})` : "";
    lines.push(`Last verify: ${String(result.status).toUpperCase()}${when}${failing.length ? ` — failing: ${failing.join("; ")}` : ""}`);
    const owners = suggestedOwners(issue, result);
    if (owners.length) lines.push(`Suggested to fix: ${owners.join(", ")}`);
  }
  return { title: `Memory issue ${issue.ref}: ${issue.title}`, description: lines.filter(Boolean).join("\n") };
}

function Details({ issue, result }) {
  const measured = Object.entries(result.measured || {});
  return (
    <VStack align="stretch" spacing={2} mt={3} mb={1}>
      {issue.why && (
        <Box bg="orange.50" borderLeftWidth="4px" borderLeftColor="orange.400" borderRadius="md" px={3} py={2}
             data-testid={`why-${issue.id}`}>
          <Text fontSize="xs" fontWeight="bold" color="orange.700" mb={1}>
            {result.status === "pass" ? "WHY THIS MATTERED" : "WHY THIS IS A MEMORY PROBLEM"}
          </Text>
          <Text fontSize="sm">{issue.why}</Text>
        </Box>
      )}
      <Box bg="blue.50" borderLeftWidth="4px" borderLeftColor="blue.400" borderRadius="md" px={3} py={2}>
        <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={1}>WHAT MAKES THIS PASS</Text>
        <Text fontSize="sm">{result.fix || issue.fix}</Text>
      </Box>
      <Box bg="gray.50" borderLeftWidth="4px" borderLeftColor="gray.400" borderRadius="md" px={3} py={2}>
        <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>CHECKS</Text>
        <VStack align="stretch" spacing={0.5}>
          {(result.checks || []).map((c, i) => (
            <HStack key={`${c.name}-${i}`} fontSize="xs" align="baseline">
              <Text color={c.passed === null ? "gray.500" : c.passed ? "green.600" : "red.600"} fontWeight="bold">
                {c.passed === null ? "·" : c.passed ? "✓" : "✕"}
              </Text>
              <Text>{c.name}</Text>
              {c.detail && <Text color={c.passed === false ? "red.700" : "gray.600"}>{"—"} {c.detail}</Text>}
            </HStack>
          ))}
        </VStack>
      </Box>
      {measured.length > 0 && (
        <Box bg="purple.50" borderLeftWidth="4px" borderLeftColor="purple.400" borderRadius="md" px={3} py={2}>
          <Text fontSize="xs" fontWeight="bold" color="purple.700" mb={1}>MEASURED</Text>
          <HStack spacing={3} flexWrap="wrap">
            {measured.map(([k, v]) => (
              <Code key={k} fontSize="xs" colorScheme="purple">{k} = {String(v)}</Code>
            ))}
          </HStack>
        </Box>
      )}
      <Text fontSize="xs" color="gray.600">
        Trigger: {issue.trigger} {"·"} review ref {issue.ref} {"·"} ran in {result.ran_ms} ms
      </Text>
      {result.error && (
        <Alert status="error" fontSize="xs" borderRadius="md"><AlertIcon />{result.error}</Alert>
      )}
    </VStack>
  );
}

function IssueRow({ issue, entry, impact, busy, error, expanded, onVerify, onToggle, task, canAssign, onAssign }) {
  const layer = LAYER[issue.layer] || LAYER.admin;
  const result = entry?.result || null;
  const verdict = result ? (VERDICT[result.status] || VERDICT.error) : null;
  return (
    <Box borderBottomWidth="1px" borderColor="gray.100" py={3} data-testid={`row-${issue.id}`}>
      <HStack justify="space-between" align="flex-start">
        <Box flex="1" minW={0}>
          <HStack spacing={3} align="baseline" flexWrap="wrap">
            <Badge colorScheme={layer.scheme} fontSize="xs" data-testid={`row-layer-${issue.id}`}>
              {layer.label}
            </Badge>
            <Text fontSize="md" fontWeight="medium">{issue.title}</Text>
            {verdict && (
              <Badge colorScheme={verdict.scheme} fontSize="xs" data-testid={`row-verdict-${issue.id}`}>
                {verdict.label}
              </Badge>
            )}
            {result && <ImpactChip impact={impact} testId={`row-impact-${issue.id}`} />}
            {result && (
              <Button size="xs" variant="link" colorScheme="blue" data-testid={`details-${issue.id}`}
                      onClick={onToggle}>
                {expanded ? "Hide details ▴" : "Details ▾"}
              </Button>
            )}
            {entry?.savedAt && !busy && (
              <Text fontSize="xs" color="gray.500" data-testid={`ran-at-${issue.id}`}>
                {entry.nightly ? `last night, on ${serverLabel(entry.nightly.server)}` : formatAge(entry.savedAt)}
              </Text>
            )}
          </HStack>
          <Text fontSize="sm" color="gray.600" mt={0.5}>{issue.cost}</Text>
          {suggestedOwners(issue, result).length > 0 && (
            <Text fontSize="xs" color="red.700" mt={0.5} data-testid={`owners-${issue.id}`}
                  title="Ranked by who wrote the code this check inspects (git history)">
              Suggested to fix: {suggestedOwners(issue, result).join(", ")}
            </Text>
          )}
          {task && <SourceTaskChip task={task} testId={`task-${issue.id}`} />}
        </Box>
        <HStack spacing={2} flexShrink={0}>
          <Button size="sm" variant="ghost" data-testid={`assign-${issue.id}`}
                  isDisabled={!canAssign} onClick={onAssign}>
            Assign task
          </Button>
          <Button size="sm" variant="outline" colorScheme={verdict?.scheme === "green" ? "green" : "blue"}
                  isLoading={busy} data-testid={`verify-${issue.id}`} onClick={onVerify}>
            {result ? "Re-verify" : "Verify"}
          </Button>
        </HStack>
      </HStack>
      {error && <Alert status="error" fontSize="xs" mt={2}><AlertIcon />{error}</Alert>}
      <Collapse in={Boolean(result) && expanded} animateOpacity unmountOnExit>
        {result && <Details issue={issue} result={result} />}
      </Collapse>
    </Box>
  );
}

/** Each check's result: from this browser's last Verify, or last night's run when that's newer. */
export function mergeProbeEntries(cached, run) {
  if (run?.status !== "ok" || !run.report?.results) return cached;
  const at = run.finished_at || run.started_at;
  const out = { ...cached };
  Object.entries(run.report.results).forEach(([id, result]) => {
    const mine = cached[id];
    if (!mine?.savedAt || Date.parse(mine.savedAt) < Date.parse(at)) out[id] = { result, savedAt: at, nightly: run };
  });
  return out;
}

// The code scan in progress, per scan function, so leaving the tab mid-scan doesn't lose it.
const scansInFlight = new WeakMap();

function startScan(scan) {
  let running = scansInFlight.get(scan);
  if (!running) {
    running = Promise.resolve()
      .then(() => scan())
      .then((result) => {
        writeCache(MEMORY_SCAN_KEY, result);
        return result;
      })
      .finally(() => scansInFlight.delete(scan));
    scansInFlight.set(scan, running);
  }
  return running;
}

function placeText(p) {
  return `${p.file}:${p.line}${p.detail ? ` — ${p.detail}` : ""}`;
}

/** Title and description for a task made from one code pattern the scan found. */
export function patternTaskText(pattern, result) {
  const places = result?.places || [];
  const total = result?.count ?? places.length;
  const lines = [
    `Found: ${result?.headline || ""}`,
    pattern.why && `Why it matters: ${pattern.why}`,
    pattern.fix && `What to do: ${pattern.fix}`,
    "Where:",
    ...places.slice(0, TASK_PLACES).map((p) => `  ${placeText(p)}`),
    total > TASK_PLACES && `  …and ${total - TASK_PLACES} more (see the Memory Issues tab)`,
    result?.owners?.length && `Suggested to fix: ${result.owners.join(", ")}`,
    `Found by the memory code scan · pattern id ${pattern.id}`,
  ];
  return { title: `Memory pattern: ${pattern.title}`, description: lines.filter(Boolean).join("\n") };
}

function ScanPatternRow({ pattern, result, impact, expanded, onToggle, task, canAssign, onAssign }) {
  const layer = LAYER[pattern.layer] || LAYER.admin;
  const verdict = result ? (SCAN_VERDICT[result.status] || SCAN_VERDICT.unknown) : null;
  const count = result?.count || 0;
  const places = result?.places || [];
  return (
    <Box borderBottomWidth="1px" borderColor="gray.100" py={3} data-testid={`pattern-${pattern.id}`}>
      <HStack justify="space-between" align="flex-start">
        <Box flex="1" minW={0}>
          <HStack spacing={3} align="baseline" flexWrap="wrap">
            <Badge colorScheme={layer.scheme} fontSize="xs">{layer.label}</Badge>
            <Badge variant="outline" colorScheme="blue" fontSize="2xs">CODE SCAN</Badge>
            <Text fontSize="md" fontWeight="medium">{pattern.title}</Text>
            {verdict && (
              <Badge colorScheme={verdict.scheme} fontSize="xs" data-testid={`pattern-verdict-${pattern.id}`}>
                {verdict.label}{count ? ` · ${count}` : ""}
              </Badge>
            )}
            {result && <ImpactChip impact={impact} testId={`pattern-impact-${pattern.id}`} />}
            <Button size="xs" variant="link" colorScheme="blue" data-testid={`pattern-details-${pattern.id}`}
                    onClick={onToggle}>
              {expanded ? "Hide details ▴" : "Details ▾"}
            </Button>
          </HStack>
          <Text fontSize="sm" color="gray.600" mt={0.5}>{result ? result.headline : "Not scanned yet."}</Text>
          {count > 0 && result.owners?.length > 0 && (
            <Text fontSize="xs" color="red.700" mt={0.5} data-testid={`pattern-owners-${pattern.id}`}
                  title="Ranked by who wrote the lines found (git history)">
              Suggested to fix: {result.owners.join(", ")}
            </Text>
          )}
          {task && <SourceTaskChip task={task} testId={`task-${pattern.id}`} />}
        </Box>
        <Button size="sm" variant="ghost" flexShrink={0} data-testid={`assign-${pattern.id}`}
                isDisabled={!canAssign || !count} onClick={onAssign}>
          Assign task
        </Button>
      </HStack>
      <Collapse in={expanded} animateOpacity unmountOnExit>
        <VStack align="stretch" spacing={2} mt={3} mb={1}>
          {pattern.why && (
            <Box bg="orange.50" borderLeftWidth="4px" borderLeftColor="orange.400" borderRadius="md" px={3} py={2}>
              <Text fontSize="xs" fontWeight="bold" color="orange.700" mb={1}>WHY THIS COSTS MEMORY</Text>
              <Text fontSize="sm">{pattern.why}</Text>
            </Box>
          )}
          {pattern.fix && (
            <Box bg="blue.50" borderLeftWidth="4px" borderLeftColor="blue.400" borderRadius="md" px={3} py={2}>
              <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={1}>WHAT TO DO</Text>
              <Text fontSize="sm">{pattern.fix}</Text>
            </Box>
          )}
          {places.length > 0 && (
            <Box bg="gray.50" borderLeftWidth="4px" borderLeftColor="gray.400" borderRadius="md" px={3} py={2}
                 data-testid={`pattern-places-${pattern.id}`}>
              <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>WHERE</Text>
              <VStack align="stretch" spacing={0.5}>
                {places.slice(0, PLACES_SHOWN).map((p) => (
                  <Code key={`${p.file}:${p.line}`} fontSize="xs" bg="transparent">{placeText(p)}</Code>
                ))}
                {count > Math.min(places.length, PLACES_SHOWN) && (
                  <Text fontSize="xs" color="gray.600">…and {count - Math.min(places.length, PLACES_SHOWN)} more</Text>
                )}
              </VStack>
            </Box>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
}

function plural(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

export default function MemoryIssuesPanel({ load, verify, scan, loadFindings, dismissFinding, loadLastNight,
                                            startSizing, loadSizing, sizingPollMs }) {
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [cached, setCached] = useState(readAllCached);
  const [busy, setBusy] = useState({});
  const [errors, setErrors] = useState({});
  const [expanded, setExpanded] = useState({});
  const [runningAll, setRunningAll] = useState(false);
  const [assigning, setAssigning] = useState(null); // { kind: "issue" | "pattern" | "ai", item }
  const [failedOnly, setFailedOnly] = useState(false);
  const [scanEntry, setScanEntry] = useState(() => readCache(MEMORY_SCAN_KEY));
  const [scanning, setScanning] = useState(() => Boolean(scan) && scansInFlight.has(scan));
  const [scanError, setScanError] = useState(null);
  // Each row shows its task (source_kind "memory_issue", source_ref = the check, pattern or finding id).
  const { me, taskByRef: taskByIssue, reloadTasks } = useSourceTasks("memory_issue", "Memory issues");
  const probesNight = useLastNight("memory_probes", loadLastNight);
  const scanNight = useLastNight("memory_scan", loadLastNight);
  const found = useCodeFindings("memory", loadFindings, dismissFinding);
  // A manual run also sizes the findings saved without an impact (one sizing covers every tab).
  const { sizing, sizeNow } = useSizing(found, startSizing, loadSizing, sizingPollMs);
  const mounted = useRef(true);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch((e) => { if (!cancelled) setLoadError(e.message); });
    return () => { cancelled = true; };
  }, [load]);

  const followScan = useCallback((running) => {
    setScanning(true);
    setScanError(null);
    running
      .then((result) => { if (mounted.current) setScanEntry({ data: result, savedAt: new Date().toISOString() }); })
      .catch((e) => { if (mounted.current) setScanError(e?.message || String(e)); })
      .finally(() => { if (mounted.current) setScanning(false); });
  }, []);

  useEffect(() => {
    mounted.current = true;
    const running = scan && scansInFlight.get(scan);
    if (running) followScan(running);
    return () => { mounted.current = false; };
  }, [scan, followScan]);

  const verifyOne = useCallback(async (id, autoExpand) => {
    setBusy((b) => ({ ...b, [id]: true }));
    setErrors((e) => ({ ...e, [id]: null }));
    try {
      const result = await verify(id);
      const entry = { result, savedAt: new Date().toISOString() };
      cacheResult(id, result);
      setCached((prev) => ({ ...prev, [id]: entry }));
      setExpanded((x) => ({ ...x, [id]: Boolean(autoExpand) }));
    } catch (e) {
      setErrors((prev) => ({ ...prev, [id]: e.message }));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  }, [verify]);

  // One at a time: several probes at once would stack their memory spikes.
  const verifyAll = useCallback(async () => {
    if (!status || runningAll) return;
    sizeNow();
    setRunningAll(true);
    try {
      for (const issue of status.issues) {
        // eslint-disable-next-line no-await-in-loop
        await verifyOne(issue.id, false);
      }
    } finally {
      setRunningAll(false);
    }
  }, [status, runningAll, verifyOne, sizeNow]);

  const entries = useMemo(() => mergeProbeEntries(cached, probesNight.run), [cached, probesNight.run]);
  const scanShown = pickNewer(scanEntry, scanNight.run);
  const scanReport = scanShown?.data || null;

  if (loadError) {
    return <Alert status="error"><AlertIcon />Could not load the issue list: {loadError}</Alert>;
  }
  if (!status) return <Spinner />;

  const toggle = (id) => setExpanded((x) => ({ ...x, [id]: !x[id] }));

  // Live checks.
  const verified = status.issues.filter((i) => entries[i.id]?.result);
  const passed = verified.filter((i) => entries[i.id].result.status === "pass").length;
  const isFailed = (i) => ["fail", "error"].includes(entries[i.id]?.result?.status);
  const failed = verified.filter(isFailed).length;
  const fromLastNight = verified.filter((i) => entries[i.id].nightly).length;

  // Code patterns: the list the backend knows about, with the latest scan's result on each.
  const scanResults = Object.fromEntries((scanReport?.patterns || []).map((r) => [r.id, r]));
  const patterns = (status.patterns?.length ? status.patterns : scanReport?.patterns || [])
    .map((p) => ({ ...p, layer: p.layer || scanResults[p.id]?.layer }));
  const patternFound = (p) => (scanResults[p.id]?.count || 0) > 0;
  const patternsFound = patterns.filter(patternFound);
  const patternsClean = patterns.filter((p) => scanResults[p.id]?.status === "ok").length;
  const patternsScanned = patterns.filter((p) => scanResults[p.id]).length;
  const places = patternsFound.reduce((n, p) => n + (scanResults[p.id].count || 0), 0);

  // The nightly AI review's open memory findings, by layer ("other" when it named none).
  const aiFindings = found.findings || [];
  const aiLayer = (f) => (LAYER[f.kind] && f.kind !== "other" ? f.kind : "other");

  // How bad each one is, as a share of the plan's memory.
  const limitMb = limitOf(status.limit_mb, scanReport?.limit_mb);
  const scanLimitMb = limitOf(scanReport?.limit_mb, status.limit_mb);
  // Each one held all the time (the floor, climbers: wasted now) or only while it runs (spikes, admin).
  const issueImpact = Object.fromEntries(status.issues.map((i) => [
    i.id, withEffect(probeImpact(i, entries[i.id]?.result, limitMb), memoryEffect(i.layer), i.layer)]));
  const patternImpacts = Object.fromEntries(patterns.map((p) => [
    p.id, withEffect(patternImpact(scanResults[p.id], scanLimitMb), memoryEffect(p.layer), p.layer)]));
  const aiImpact = Object.fromEntries(aiFindings.map((f) => [f.id, withEffect(memoryFindingImpact(f, limitMb),
                                                                               findingEffect(f), f.kind)]));
  // What the server uses now, and used when it started: the issues can't be wasting more than it uses,
  // the idle floor more than it used at start, or slow climbers more than it has grown since.
  const sharePct = (mb) => (Number(mb) > 0 ? (Number(mb) / limitMb) * 100 : null);
  const usedPct = sharePct(status.used_mb);
  const startPct = sharePct(status.start_mb);
  const impactItems = [
    ...status.issues.map((i) => ({ key: i.id, label: i.title, impact: issueImpact[i.id], pending: !entries[i.id]?.result })),
    ...patterns.map((p) => ({ key: p.id, label: p.title, impact: patternImpacts[p.id], pending: !scanResults[p.id] })),
    ...aiFindings.map((f) => ({ key: f.id, label: f.title, impact: aiImpact[f.id] })),
  ];

  const anyChecked = verified.length > 0 || patternsScanned > 0 || aiFindings.length > 0;
  const failing = failed + patternsFound.length + aiFindings.length;
  const allPassed = anyChecked && verified.length === passed && patternsScanned === patternsClean;
  const state = stateOf(impactItems, { memory: true, usedPct, startPct });
  // Gray when nothing could be measured yet (every check run so far errored).
  const measured = impactItems.some((i) => pctOf(i) !== null);
  const tone = measured ? TONE_OF_STATE[state] : "gray";
  const badge = { red: "FIX NOW", yellow: "MINOR ISSUES", gray: "COULDN'T MEASURE" }[tone]
    || (failing ? "ONLY SMALL ISSUES" : allPassed ? "ALL FIXED" : "NOTHING FAILING");
  // The toggle lives in the summary, so the filter only applies while that is on screen.
  const filtering = failedOnly && anyChecked;
  // One list for the whole tab, most important first: everything open (failed checks, patterns found,
  // the AI review's findings) by how much of the plan's memory it costs, the worse status first between
  // two the same size, then what isn't sized yet; below it what hasn't been checked, then what passes.
  const rows = byImpact([
    ...status.issues.map((i) => ({ kind: "issue", id: i.id, item: i, impact: issueImpact[i.id], open: isFailed(i),
                                   checked: Boolean(entries[i.id]?.result), worse: worseOf(entries[i.id]?.result?.status) })),
    ...patterns.map((p) => ({ kind: "pattern", id: p.id, item: p, impact: patternImpacts[p.id], open: patternFound(p),
                              checked: Boolean(scanResults[p.id]), worse: 1 })),
    ...aiFindings.map((f) => ({ kind: "ai", id: f.id, item: f, impact: aiImpact[f.id], open: true, checked: true,
                                worse: worseOf(f.severity) })),
  ], (r) => pctOf(r.impact), (r) => r.worse);
  const openRows = rows.filter((r) => r.open);
  const uncheckedRows = rows.filter((r) => !r.open && !r.checked);
  const passingRows = rows.filter((r) => !r.open && r.checked);
  const layerCount = (k) => status.issues.filter((i) => i.layer === k).length
    + patterns.filter((p) => p.layer === k).length + aiFindings.filter((f) => aiLayer(f) === k).length;
  const layers = Object.keys(LAYER).filter((k) => layerCount(k) > 0);
  const renderRow = (r) => {
    if (r.kind === "issue") {
      const issue = r.item;
      return (
        <IssueRow key={issue.id} issue={issue} entry={entries[issue.id]} impact={issueImpact[issue.id]}
                  busy={Boolean(busy[issue.id])} error={errors[issue.id]} expanded={Boolean(expanded[issue.id])}
                  onVerify={() => verifyOne(issue.id, true)} onToggle={() => toggle(issue.id)}
                  task={taskByIssue[issue.id]} canAssign={Boolean(me)}
                  onAssign={() => setAssigning({ kind: "issue", item: issue })} />
      );
    }
    if (r.kind === "pattern") {
      const pattern = r.item;
      return (
        <ScanPatternRow key={pattern.id} pattern={pattern} result={scanResults[pattern.id]}
                        impact={patternImpacts[pattern.id]}
                        expanded={Boolean(expanded[pattern.id])} onToggle={() => toggle(pattern.id)}
                        task={taskByIssue[pattern.id]} canAssign={Boolean(me)}
                        onAssign={() => setAssigning({ kind: "pattern", item: pattern })} />
      );
    }
    const f = r.item;
    const layer = LAYER[aiLayer(f)];
    return (
      <Box key={f.id} py={2}>
        <AiFindingCard finding={f} impact={aiImpact[f.id]} task={taskByIssue[f.id]} canAssign={Boolean(me)}
                       badge={<Badge colorScheme={layer.scheme} fontSize="xs">{layer.label}</Badge>}
                       onAssign={(finding) => setAssigning({ kind: "ai", item: finding })}
                       onDismiss={found.askDismiss} TaskChip={SourceTaskChip} />
      </Box>
    );
  };

  const assignText = () => {
    if (assigning.kind === "pattern") return patternTaskText(assigning.item, scanResults[assigning.item.id]);
    if (assigning.kind === "ai") return aiFindingTaskText(assigning.item);
    return issueTaskText(assigning.item, entries[assigning.item.id]);
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold" data-testid="memory-issues-header">
            {plural(status.issues.length, "live check")}
            {patterns.length ? ` · ${plural(patterns.length, "code pattern")} looked for across the backend` : ""}
            {loadFindings && !found.loading && !found.missing
              ? ` · ${plural(aiFindings.length, "open finding")} from the nightly AI review` : ""}
          </Text>
          <Text fontSize="sm">
            Verify runs a check against this backend: a row turns green once its fix is deployed. Scan code looks
            through the whole backend for code that costs memory. The nightly AI review files the memory problems it
            finds in each day's commits here. The server runs the checks and the scan by itself each night that had
            commits.
          </Text>
        </Box>
      </Alert>
      <Divider />
      <HStack justify="space-between" flexWrap="wrap" spacing={3}>
        <HStack flexWrap="wrap">
          {layers.map((k) => (
            <Badge key={k} colorScheme={LAYER[k].scheme} title={LAYER[k].blurb}>
              {layerCount(k)} {LAYER[k].label.toLowerCase()}
            </Badge>
          ))}
        </HStack>
        <HStack spacing={2}>
          {scan && (
            <Button size="sm" variant="outline" colorScheme="blue" data-testid="scan-code" isLoading={scanning}
                    loadingText="Scanning…" onClick={() => { followScan(startScan(scan)); sizeNow(); }}>
              {scanReport ? "Scan code again" : "Scan code"}
            </Button>
          )}
          <Button size="sm" colorScheme="blue" data-testid="verify-all" isLoading={runningAll} onClick={verifyAll}>
            {verified.length ? "Re-verify All" : "Verify All"}
          </Button>
        </HStack>
      </HStack>
      {(() => {
        // One line for what's shown from last night, naming it: both usually ran minutes apart.
        const probesRun = fromLastNight > 0 || probesNight.run?.status === "failed" ? probesNight.run : null;
        const scanRun = scanShown?.source === "nightly" ? scanShown.run : null;
        if (probesRun && scanRun && probesRun.status === scanRun.status) {
          return <LastNightLabel run={probesRun} what="The live checks and the code scan" testId="memory-last-night" />;
        }
        return (
          <>
            {probesRun && <LastNightLabel run={probesRun} what="The live checks" testId="memory-last-night" />}
            {scanRun && <LastNightLabel run={scanRun} what="The code scan" testId={probesRun ? "memory-scan-last-night" : "memory-last-night"} />}
          </>
        );
      })()}
      {scanShown?.source === "live" && (
        <Text fontSize="xs" color="gray.600" data-testid="memory-scan-meta">
          Code scanned {formatAge(scanEntry?.savedAt)}
          {scanReport.duration_ms != null ? ` · took ${(scanReport.duration_ms / 1000).toFixed(1)} s` : ""}
          {scanReport.owners_from ? ` · who to ask: ${scanReport.owners_from}` : ""}
        </Text>
      )}
      {scanError && (
        <Alert status="error" borderRadius="md" fontSize="sm" data-testid="memory-scan-error">
          <AlertIcon />Couldn't scan the code: {scanError}
        </Alert>
      )}
      {anyChecked && (
        <Box px={3} py={2} borderRadius="md" borderWidth="2px" bg={`${tone}.50`} borderColor={`${tone}.400`}
             data-testid="verify-summary" data-tone={tone} data-state={state}>
          <HStack justify="space-between" align="center" spacing={4} flexWrap="wrap">
            <VStack align="start" spacing={2}>
              <HStack flexWrap="wrap">
                <Badge colorScheme={tone} fontSize="sm" px={2}>{badge}</Badge>
                <Text fontSize="sm" fontWeight="bold">
                  {verified.length > 0 ? `${passed} of ${verified.length} verified issues fixed` : ""}
                  {patternsFound.length
                    ? `${verified.length ? " · " : ""}${plural(patternsFound.length, "code pattern")} found in ${plural(places, "place")}`
                    : ""}
                  {aiFindings.length
                    ? `${verified.length || patternsFound.length ? " · " : ""}${aiFindings.length} from the nightly AI review`
                    : ""}
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.600" data-testid="memory-impact-basis">
                Each issue counts its memory as a share of {planLabel(limitMb)}
                {status.limit_source ? ` (${LIMIT_SOURCE[status.limit_source] || status.limit_source})` : ""}
                {usedPct !== null ? `, of which the server is using ${Math.round(usedPct)}% now` : ""}. The health
                counts what the issues hold all the time: never more than the server uses, the idle floor never
                more than it used at start, slow climbers never more than it has grown since. A spike's memory is
                only used while it runs.
              </Text>
              <Button size="xs" colorScheme="red" variant={failedOnly ? "solid" : "outline"}
                      aria-pressed={failedOnly} data-testid="failed-only"
                      onClick={() => setFailedOnly((on) => !on)}>
                Failed only ({failing})
              </Button>
            </VStack>
            <ImpactDonut testId="memory-issues-pie" label="Memory" items={impactItems} memory usedPct={usedPct}
                         startPct={startPct} />
          </HStack>
        </Box>
      )}
      {filtering && failing === 0 && (
        <Text fontSize="sm" color="gray.600" data-testid="failed-only-empty">
          Nothing failed in the latest verify.
        </Text>
      )}
      {openRows.length > 0 && (
        <Box data-testid="memory-open">
          <Text fontWeight="bold" mb={1}>What to fix, most important first ({openRows.length})</Text>
          <SizingNote sizing={sizing} testId="memory-sizing" />
          {openRows.map(renderRow)}
        </Box>
      )}
      {!filtering && uncheckedRows.length > 0 && (
        <Box data-testid="memory-unchecked">
          <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>Not checked yet ({uncheckedRows.length})</Text>
          {uncheckedRows.map(renderRow)}
        </Box>
      )}
      {!filtering && passingRows.length > 0 && (
        <Box data-testid="memory-passing">
          <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
            Fixed, or not found in the code ({passingRows.length})
          </Text>
          {passingRows.map(renderRow)}
        </Box>
      )}
      {loadFindings && found.missing && (
        <Alert status="info" borderRadius="md" fontSize="sm"><AlertIcon />{found.missing}</Alert>
      )}
      {loadFindings && found.error && (
        <Text fontSize="sm" color="red.600">Couldn't load the nightly AI review's findings: {found.error}</Text>
      )}
      {loadFindings && !found.loading && !found.missing && !found.error && (
        <ClosedFindings closed={found.closed} toggleClosed={found.toggleClosed} testId="memory-ai-closed" />
      )}
      {found.dismissDialog}
      {assigning && me && (() => {
        const { title, description } = assignText();
        return (
          <AssignModal
            isOpen
            onClose={() => setAssigning(null)}
            currentUser={me}
            taskOnly
            initialTitle={title}
            initialDescription={description}
            taskSource={{ source_kind: "memory_issue", source_ref: assigning.item.id }}
            onAssigned={reloadTasks}
          />
        );
      })()}
    </VStack>
  );
}
